import { appStorage } from '../../lib/betaStorage'
import type { RecipeDetail, RecipeSummary } from './types'

const API_ROOT = 'https://www.themealdb.com/api/json/v1/1'
const CACHE_KEY = 'marnixia.recipeCache.v1'

type MealDbMeal = Record<string, string | null>

const fallbacks: RecipeDetail[] = [
  { id: 'fallback-pasta', name: 'Tomato pasta', thumbnail: '', category: 'Everyday', ingredients: [{ name: 'Pasta', measure: '1 packet' }, { name: 'Tomato sauce', measure: '1 jar' }, { name: 'Parmesan', measure: 'To serve' }], steps: ['Cook the pasta according to the packet.', 'Warm the tomato sauce.', 'Drain the pasta and combine with the sauce.', 'Serve carefully and check the temperature.'] },
  { id: 'fallback-chicken', name: 'Chicken and vegetables', thumbnail: '', category: 'Everyday', ingredients: [{ name: 'Chicken breast', measure: '2 pieces' }, { name: 'Mixed vegetables', measure: '3 cups' }, { name: 'Olive oil', measure: '1 tablespoon' }], steps: ['Prepare the chicken and vegetables on separate clean surfaces.', 'Cook the chicken completely.', 'Cook the vegetables until tender.', 'Check the chicken is cooked through before serving.'] },
  { id: 'fallback-wrap', name: 'Build-your-own wraps', thumbnail: '', category: 'Easy choice', ingredients: [{ name: 'Wraps', measure: '4' }, { name: 'Chosen filling', measure: '2 cups' }, { name: 'Salad vegetables', measure: 'As preferred' }], steps: ['Place each ingredient in a separate bowl.', 'Choose preferred fillings.', 'Add fillings to the wrap.', 'Fold and serve.'] },
  { id: 'fallback-soup', name: 'Vegetable soup', thumbnail: '', category: 'Comfort meal', ingredients: [{ name: 'Mixed vegetables', measure: '4 cups' }, { name: 'Low-salt stock', measure: '1 litre' }, { name: 'Bread', measure: 'To serve' }], steps: ['Wash and cut the vegetables.', 'Add vegetables and stock to a pot.', 'Simmer until vegetables are soft.', 'Blend only if appropriate for Alex’s texture needs.', 'Allow to cool to a safe temperature.'] },
]

function summary(meal: MealDbMeal): RecipeSummary {
  return { id: meal.idMeal ?? '', name: meal.strMeal ?? 'Unnamed recipe', thumbnail: meal.strMealThumb ?? '', category: meal.strCategory ?? undefined, area: meal.strArea ?? undefined }
}

function detail(meal: MealDbMeal): RecipeDetail {
  const ingredients: RecipeDetail['ingredients'] = []
  for (let index = 1; index <= 20; index += 1) {
    const name = meal[`strIngredient${index}`]?.trim(); const measure = meal[`strMeasure${index}`]?.trim() ?? ''
    if (name) ingredients.push({ name, measure })
  }
  const instructions = meal.strInstructions?.trim() ?? ''
  const steps = instructions.split(/(?:\r?\n)+|(?<=\.)\s+(?=[A-Z])/).map(item => item.trim()).filter(Boolean)
  return { ...summary(meal), ingredients, steps: steps.length ? steps : ['Follow the recipe instructions supplied by the family.'], sourceUrl: meal.strSource ?? undefined }
}

export async function browseRecipes(signal?: AbortSignal): Promise<{ recipes: RecipeSummary[]; offline: boolean }> {
  try {
    const response = await fetch(`${API_ROOT}/filter.php?c=Chicken`, { signal })
    if (!response.ok) throw new Error('Recipe service unavailable')
    const json = await response.json() as { meals?: MealDbMeal[] | null }
    const recipes = (json.meals ?? []).slice(0, 16).map(summary).filter(item => item.id && item.name)
    if (!recipes.length) throw new Error('No recipes returned')
    try { appStorage.setItem(CACHE_KEY, JSON.stringify(recipes)) } catch { /* storage is optional */ }
    return { recipes, offline: false }
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error
    try { const cached = JSON.parse(appStorage.getItem(CACHE_KEY) ?? '[]') as RecipeSummary[]; if (cached.length) return { recipes: cached, offline: true } } catch { /* use bundled fallback */ }
    return { recipes: fallbacks, offline: true }
  }
}

export async function loadRecipe(recipe: RecipeSummary, signal?: AbortSignal): Promise<RecipeDetail> {
  const local = fallbacks.find(item => item.id === recipe.id)
  if (local) return local
  try {
    const response = await fetch(`${API_ROOT}/lookup.php?i=${encodeURIComponent(recipe.id)}`, { signal })
    if (!response.ok) throw new Error('Recipe details unavailable')
    const json = await response.json() as { meals?: MealDbMeal[] | null }; const meal = json.meals?.[0]
    if (!meal) throw new Error('Recipe not found')
    return detail(meal)
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error
    return { ...recipe, ingredients: [], steps: ['Recipe details are unavailable offline. Use the saved family instructions or try again when connected.'] }
  }
}


